import { SNCR_PRESCRIPTION_TYPES } from './constants';
import { AlertCode, ResponseAlert } from './sncrTypes';

export interface SncrNotificationDisplay {
  label: string;
  shortLabel: string;
  bg: string;
  color: string;
  border?: string;
}

const TYPE_BY_LIST_CODE: Record<string, string> = {
  A1: 'NRA',
  A2: 'NRA',
  A3: 'NRA',
  B1: 'NRB',
  B2: 'NRB2',
  C1: 'RCE',
  C2: 'NRR',
  C3: 'NRT',
  C4: 'RCE',
  C5: 'RCE',
  D1: 'RET',
};

function tipoSncrFromPortaria344(portaria344?: string): string | null {
  if (!portaria344) return null;

  const value = portaria344.toUpperCase();
  const code = value.match(/LISTA\s+([ABCD]\d)/)?.[1];

  if (value.includes('ADENDO')) {
    if (!code) return null;
    return code.startsWith('C') ? 'RET' : 'RCE';
  }
  if (code) return TYPE_BY_LIST_CODE[code] ?? null;
  return value.includes('RDC 405') ? 'RCE' : null;
}

/**
 * Resolve a notificação SNCR (cor + label) para exibir a fita do medicamento.
 *
 * Aceita o `tipoSncr` já resolvido (disponível depois que o item entra na
 * prescrição) ou, na sua ausência, deriva a partir da string `portaria344`
 * (disponível na modal de edição antes do item ser adicionado).
 *
 * @param source - `tipoSncr` tem precedência sobre `portaria344`.
 * @returns Estilo, label por extenso (`label`, ex.: "Notificação de Receita A
 * (NRA)") e sigla (`shortLabel`, ex.: "NRA") da fita, ou `null` quando não é um
 * tipo notificável.
 */
export function getSncrNotificationDisplay(source: {
  tipoSncr?: string;
  portaria344?: string;
}): SncrNotificationDisplay | null {
  const tipoSncr = source.tipoSncr?.toUpperCase() || tipoSncrFromPortaria344(source.portaria344);
  if (!tipoSncr) return null;

  const config = SNCR_PRESCRIPTION_TYPES.find((type) => type.id === tipoSncr);
  if (!config) return null;

  return {
    label: `${config.name} (${config.id})`,
    shortLabel: config.id,
    ...config.tag,
  };
}

export interface SncrAlertFeedback {
  severity: 'success' | 'warning';
  subtitle: string;
}

/**
 * Alertas com subtítulo fixo. O `SNCR_QUOTA_RESERVED` fica de fora porque seu
 * subtítulo é dinâmico (inclui o número reservado) e é tratado à parte.
 */
const STATIC_ALERT_FEEDBACK: Partial<Record<AlertCode, SncrAlertFeedback>> = {
  [AlertCode.SNCR_INSUFFICIENT_QUOTA]: { severity: 'warning', subtitle: 'Sem saldo para reserva' },
  [AlertCode.SNCR_RESERVE_QUOTA_ERROR]: { severity: 'warning', subtitle: 'Não foi possível reservar a numeração SNCR' },
};

/**
 * Traduz os `metadata.alerts` retornados ao adicionar um medicamento no
 * subtítulo e na severidade do toast de confirmação.
 *
 * @param alerts - Alertas da resposta de `/prescricao/adicionarmedicamento`.
 * @returns Severidade + subtítulo para o toast, ou `undefined` quando não há
 * alerta SNCR relevante (mantém o toast de sucesso padrão, sem subtítulo).
 */
export function buildSncrAlertFeedback(alerts?: ResponseAlert[]): SncrAlertFeedback | undefined {
  const alert = alerts?.find(
    (item) => item.code === AlertCode.SNCR_QUOTA_RESERVED || item.code in STATIC_ALERT_FEEDBACK
  );
  if (!alert) return undefined;

  if (alert.code === AlertCode.SNCR_QUOTA_RESERVED) {
    const number = alert.data?.number;
    return number ? { severity: 'success', subtitle: `Número SNCR reservado com sucesso: ${number}` } : undefined;
  }

  return STATIC_ALERT_FEEDBACK[alert.code];
}
